/**
 * Hook for fetching locale options (available languages, countries with currencies)
 * from the v2 locale API
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchLocaleOptions } from "@/api/locale";
import type { LocaleOptions } from "@/api/locale";

interface UseLocaleOptionsResult {
  localeOptions: LocaleOptions | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useLocaleOptions(): UseLocaleOptionsResult {
  const [localeOptions, setLocaleOptions] = useState<LocaleOptions | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const result = await fetchLocaleOptions();

    if (controller.signal.aborted) return;

    if (result.ok) {
      setLocaleOptions(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    localeOptions,
    isLoading,
    error,
    refetch: load,
  };
}
